import { forcePromise } from "../utils";
import { WorkerQueryTransferModes } from "./consts";
import { decodeJSONBuffer } from "./encoding";
import { listenForResponse } from "./listeners";
import { sendQueryResult } from "./send";
import { MessagePayload, WorkerQueryOptions } from "./types";

/**
 * Utility per worker: gestisce input da SharedArrayBuffer o normale
 */
export const handleWorkerInput = (event: MessageEvent<MessagePayload<any>>) => {
  const { type, data, opts } = event.data;

  const decodedPayload = decodeJSONBuffer(data, {
    useCompression: opts?.request?.useCompression,
  });

  return { type, payload: decodedPayload, opts };
};

export type WebWorkerQueryChunkedOptions = WorkerQueryOptions & {
  transferMode: typeof WorkerQueryTransferModes.CHUNKED;
  chunkSize?: number;
  traceKey?: string;
  skipEncoding?: boolean;
  compress?: boolean;
};

export const createWorkerHandler = (
  handlers: Record<
    string,
    (
      payload: MessagePayload<any>["data"],
      opts?: WorkerQueryOptions
    ) => Promise<any> | any
  >
) => {
  return (event: MessageEvent<MessagePayload<any>>) => {
    const { type, payload, opts } = handleWorkerInput(event);

    const traceKey = opts?.traceKey || "anonymous query";

    if (handlers[type]) {
      const result = handlers[type](payload, opts);

      forcePromise(result).then((resolvedResult) => {
        sendQueryResult(
          self,
          resolvedResult,
          resolvedResult.type,
          {
            id: event.data.requestId,
            type: event.data.type,
            timestamp: event.timeStamp,
          },
          opts
        ).catch((error) => {
          console.error(
            `[${traceKey}] Error sending query result for type ${type}:`,
            error
          );
        });
      });
    } else {
      console.warn(`[${traceKey}] Unknown message type: ${type}`);
    }
  };
};

/**
 *  Sends a query to a Web Worker using provided  for efficient data transfer.
 * @param worker  The Web Worker to communicate with.
 * @param data The query data, including type, payload, and options.
 * @returns A promise that resolves with the response from the worker, either as a JSON object or SharedArrayBuffer.
 */
export function webWorkerQuery<Q = any, T = any>(
  worker: Worker,
  data: Omit<MessagePayload<Q>, "requestId">
): Promise<T | null> {
  console.log("Posting to Web Worker:", data);
  return new Promise<T | null>((resolve, reject) => {
    const requestId = `${data.type}-${Date.now()}-${Math.random()}`;

    listenForResponse<T>(worker, { ...data, requestId })
      .then(resolve)
      .catch(reject);

    worker.postMessage({ ...data, requestId });
    console.log("Posted to Web Worker:", { ...data, requestId });
  });
}
